import { useState } from "react";
import { Loader2, ShieldCheck, KeyRound, Mail, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";

const METHOD_ICONS = { totp: ShieldCheck, sms: MessageSquare, email: Mail, recovery: KeyRound };

// Passo 2 del login: codice dall'app Authenticator (default), oppure SMS/email
// (se abilitati dall'admin) o codice di recupero monouso. Il token arriva SOLO
// dopo la verifica: niente accesso con la sola password.
export function TwoFactorStep({ preToken, methods, onSuccess, onCancel }) {
  const { t } = useLanguage();
  const available = [...(methods || [])];
  const [method, setMethod] = useState(available.includes("totp") ? "totp" : available[0] || "totp");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const [useRecovery, setUseRecovery] = useState(false);

  const effectiveMethod = useRecovery ? "recovery" : method;

  const sendChannelCode = async (m) => {
    setBusy(true);
    try {
      await api.post("/2fa/login/send-code", { pre_token: preToken, method: m });
      setCodeSent(true);
      toast.success(t("tfa.codeSent"));
    } catch (err) {
      const d = err.response?.data?.detail || "";
      toast.error(d === "2fa_code_too_soon" ? t("tfa.codeTooSoon") : t(`tfa.err.${d}`,) || t("misc.error"));
    } finally {
      setBusy(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/2fa/login/verify", {
        pre_token: preToken, method: effectiveMethod, code: code.trim(),
      });
      onSuccess(data);
    } catch (err) {
      const d = err.response?.data?.detail || "";
      if (d === "2fa_session_expired" || d === "2fa_session_invalid") {
        toast.error(t("tfa.sessionExpired"));
        onCancel();
      } else if (d === "2fa_too_many_attempts") {
        toast.error(t("tfa.tooMany"));
        onCancel();
      } else {
        toast.error(t("tfa.invalidCode"));
      }
    } finally {
      setBusy(false);
    }
  };

  const ActiveIcon = useRecovery ? KeyRound : (METHOD_ICONS[method] || ShieldCheck);

  return (
    <form data-testid="twofa-step" onSubmit={submit} className="space-y-4">
      <div className="flex items-center gap-3 rounded-2xl bg-orange-50 border border-orange-200 px-4 py-3">
        <ActiveIcon className="w-5 h-5 text-[#FF6B35] shrink-0" />
        <p className="text-sm text-slate-700">
          {useRecovery ? t("tfa.recoveryHint")
            : method === "totp" ? t("tfa.totpHint")
            : method === "sms" ? t("tfa.smsHint") : t("tfa.emailHint")}
        </p>
      </div>

      {!useRecovery && (available.includes("sms") || available.includes("email")) && (
        <div className="flex gap-2">
          {available.map((m) => (
            <button key={m} type="button" data-testid={`twofa-method-${m}`}
              onClick={() => { setMethod(m); setCodeSent(false); setCode(""); }}
              className={`flex-1 rounded-full px-3 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest transition-colors ${
                method === m ? "bg-[#1E293B] text-white" : "border border-[var(--border-strong)] text-[var(--text-secondary)] hover:border-[#FF6B35]"}`}>
              {m === "totp" ? "Authenticator" : m.toUpperCase()}
            </button>
          ))}
        </div>
      )}

      {!useRecovery && (method === "sms" || method === "email") && !codeSent && (
        <button type="button" data-testid="twofa-send-code" onClick={() => sendChannelCode(method)} disabled={busy}
          className="w-full rounded-full border border-[var(--border-strong)] px-6 py-3 font-mono-t text-xs uppercase tracking-[0.2em] hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors disabled:opacity-60">
          {busy && <Loader2 className="inline w-4 h-4 animate-spin mr-2" />}
          {method === "sms" ? t("tfa.sendSms") : t("tfa.sendEmail")}
        </button>
      )}

      <input data-testid="twofa-code-input" value={code} required autoFocus
        inputMode={useRecovery ? "text" : "numeric"} autoComplete="one-time-code"
        maxLength={useRecovery ? 12 : 6}
        onChange={(e) => setCode(useRecovery ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, ""))}
        placeholder={useRecovery ? t("tfa.recoveryPlaceholder") : "000000"}
        className="w-full rounded-2xl border border-[var(--border-strong)] bg-transparent px-5 py-3 text-center font-mono-t text-2xl tracking-[0.4em] outline-none focus:border-[#FF6B35] transition-colors" />

      <button data-testid="twofa-verify-button" type="submit" disabled={busy || code.trim().length < 6}
        className="w-full rounded-full bg-[var(--bg-dark)] text-[var(--text-on-dark)] px-6 py-3.5 font-mono-t text-xs uppercase tracking-[0.2em] hover:bg-[#FF6B35] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
        {busy && <Loader2 className="w-4 h-4 animate-spin" />}
        {t("tfa.verify")}
      </button>

      <div className="flex items-center justify-between text-sm">
        <button type="button" data-testid="twofa-toggle-recovery" onClick={() => { setUseRecovery(!useRecovery); setCode(""); }}
          className="text-[#FF6B35] hover:underline">
          {useRecovery ? t("tfa.useApp") : t("tfa.useRecovery")}
        </button>
        <button type="button" data-testid="twofa-cancel" onClick={onCancel} className="text-[var(--text-secondary)] hover:underline">
          {t("tfa.backToLogin")}
        </button>
      </div>
    </form>
  );
}
